import { useState, useEffect } from "react";
import {
  FileText,
  Sparkles,
  MoreHorizontal,
  TrendingUp,
  Clock,
} from "lucide-react";
import medicalDB from "../services/database";

const DraftSuggestionChips = ({ onSelectDraft, onShowAllTemplates }) => {
  const [popularDrafts, setPopularDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPopularDrafts();
  }, []);

  const loadPopularDrafts = async () => {
    try {
      setLoading(true);
      // Get top 3 most popular drafts for chips
      const popular = await medicalDB.getPopularDrafts(3);
      setPopularDrafts(popular);
    } catch (error) {
      console.error("Error loading popular drafts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDraft = async (draft) => {
    try {
      // Increment usage count when selected from chips
      await medicalDB.incrementDraftUsage(draft.id);
      onSelectDraft(draft);
    } catch (error) {
      console.error("Error updating draft usage:", error);
      // Still proceed with selection even if usage tracking fails
      onSelectDraft(draft);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Sparkles className="h-5 w-5 text-medical-blue" />
          <span className="text-sm font-medium text-medical-blue">
            Quick Start Templates
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="animate-pulse bg-gray-200 rounded-full h-8 w-32"></div>
          <div className="animate-pulse bg-gray-200 rounded-full h-8 w-28"></div>
          <div className="animate-pulse bg-gray-200 rounded-full h-8 w-36"></div>
        </div>
      </div>
    );
  }

  // Don't show the section if there are no popular drafts
  if (popularDrafts.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
      <div className="flex items-center space-x-2 mb-3">
        <Sparkles className="h-5 w-5 text-medical-blue" />
        <span className="text-sm font-medium text-medical-blue">
          Quick Start Templates
        </span>
        <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
          Popular
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Popular Draft Chips */}
        {popularDrafts.map((draft) => (
          <button
            key={draft.id}
            onClick={() => handleSelectDraft(draft)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-blue-200 rounded-full text-sm font-medium text-medical-blue hover:bg-blue-50 hover:border-blue-300 transition-colors shadow-sm"
          >
            <FileText size={14} />
            <span>{draft.name}</span>
            {draft.usageCount > 0 && (
              <span className="inline-flex items-center text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">
                <TrendingUp size={10} className="mr-1" />
                {draft.usageCount}
              </span>
            )}
          </button>
        ))}

        {/* Other Templates Button */}
        <button
          onClick={onShowAllTemplates}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
        >
          <MoreHorizontal size={14} />
          <span>Other Templates</span>
        </button>

        {/* Skip Templates Option */}
        <span className="text-xs text-gray-500 ml-2">
          or continue without a template
        </span>
      </div>

      {/* Helper Text */}
      <div className="mt-3 text-xs text-blue-700 bg-blue-100 bg-opacity-50 rounded-md p-2">
        <div className="flex items-start space-x-1">
          <FileText size={12} className="mt-0.5 flex-shrink-0" />
          <span>
            Templates pre-fill common visit information to save time. You can
            edit all fields after selecting a template.
          </span>
        </div>
      </div>
    </div>
  );
};

export default DraftSuggestionChips;
